import React, { useState, useEffect } from 'react';
import { apiPost } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';
import { BTN } from '../constants/buttons';
import { useAuth } from '../contexts/AuthContext';
import { FIELD_LABEL_CLASS } from '../constants/inputs';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../constants/overlayLayers';

/**
 * GlobalOffboardModal — Site-Admin-only nuclear path.
 *
 * Calls POST /api/users/:id/offboard to remove the user from ALL productions
 * and deactivate their account globally. Defence-in-depth on the server
 * requires `cascade_all_productions=true` when the user is on multiple
 * productions (returns 409 multi_production_user otherwise — see bd5e916).
 *
 * Flow:
 *   1. On mount, load the preflight via ?check=true. Surfaces counts +
 *      `guards.multi_production` + `productions` list.
 *   2. Admin reviews the blast radius (linked-data counts + productions list).
 *   3. Admin nominates a successor for every department this offboard would
 *      leave with nobody able to run its orders (`guards.buyer_replacement_required`,
 *      each entry carrying its own `candidates`). ⚖️ Added 2026-09-20 by owner
 *      ruling: this used to be a raw 409 with no way forward from the dialog.
 *   4. Admin ticks cascade checkbox if multi-prod (required to enable Confirm).
 *   5. Admin optionally ticks GDPR erasure for Hireforge listings.
 *   6. Admin types user's display name to confirm.
 *   7. Confirm → POST { confirm_name, cascade_all_productions, erasure_request,
 *      reassignments? }.
 *      `confirm_name` is NOT optional: the server re-checks it against the
 *      target's display_name and 400s (`confirm_name_required`) without it,
 *      so the typed name must actually travel on the wire — the client-side
 *      button gate alone is not the guard, it only mirrors it.
 *
 * Props:
 *   user: { id: number, display_name: string }
 *   onClose: () => void
 *   onSuccess: (data) => void
 */
export default function GlobalOffboardModal({ user, onClose, onSuccess }) {
  const { user: authUser } = useAuth();
  const isGlobalAdmin = authUser?.isAdmin;

  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);

  const [cascadeAllProductions, setCascadeAllProductions] = useState(false);
  const [erasureRequest, setErasureRequest] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  // { [departmentId]: userId } — the admin's nominations, exactly the shape
  // `body.reassignments.buyer_replacements` travels in.
  const [buyerReplacements, setBuyerReplacements] = useState({});

  // Load preflight on mount. Re-runs only when target user changes.
  useEffect(() => {
    if (!isGlobalAdmin) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    async function loadPreflight() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiPost(`/api/users/${user.id}/offboard?check=true`, {});
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load preflight summary');
        if (!cancelled) setPreflight(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadPreflight();
    return () => {
      cancelled = true;
    };
  }, [user.id, isGlobalAdmin]);

  const multiProduction = !!preflight?.guards?.multi_production;
  const isSelf = !!preflight?.guards?.is_self;
  const isLastAdmin = !!preflight?.guards?.is_last_admin;
  const confirmNameMatches = confirmName === user.display_name;

  // ⚖️ RULED (Pete, 2026-09-20): a department this offboard would leave with
  // nobody able to run its orders must be an ACTIONABLE BLOCK WITH A PICKER,
  // not the raw 409 the admin used to meet. The server computes both the list
  // and each department's candidates in the pre-flight it already returns.
  //
  // 🪤 THE NAME ON THE WIRE IS STILL `buyer_replacements`, AND IT IS NO LONGER
  //    A ROLE NAME — it is the historic key of an API field. What the server
  //    demands is a successor who can run the department; what it GRANTS is
  //    `can_manage_department_orders`, and it writes nothing at all to a
  //    nominee who already has it. Do not "tidy" this copy back into buyer
  //    language: the whole point of the 2026-09-21 fix is that a supervisor is
  //    an equally valid answer.
  const replacementsRequired = Array.isArray(preflight?.guards?.buyer_replacement_required)
    ? preflight.guards.buyer_replacement_required
    : [];
  // A department with NO candidates cannot be resolved from this dialog at all
  // — there is nobody left in it to nominate. Surfaced as its own message
  // rather than an empty dropdown, because an empty dropdown reads as a bug.
  const unresolvableDepartments = replacementsRequired.filter(
    (dep) => !Array.isArray(dep.candidates) || dep.candidates.length === 0
  );
  const allReplacementsChosen = replacementsRequired.every(
    (dep) => !!buyerReplacements[dep.department_id]
  );

  const hardBlocks = [];
  if (isSelf) hardBlocks.push('You cannot deactivate yourself.');
  if (isLastAdmin) hardBlocks.push('This is the only active global admin. Promote another user to admin first.');
  for (const dep of unresolvableDepartments) {
    hardBlocks.push(
      `“${dep.department_name}” would be left with nobody able to run its orders, and it has no `
      + 'other active member to hand it to. Add someone to that department first.'
    );
  }

  const canConfirm =
    !executing &&
    isGlobalAdmin &&
    preflight &&
    hardBlocks.length === 0 &&
    confirmNameMatches &&
    // Every stranded department has a nominee. Server enforces the same with a
    // 409 naming the department — this gate exists so the admin is never able
    // to press Confirm on a request that cannot succeed.
    allReplacementsChosen &&
    // Multi-prod users require explicit cascade opt-in. Server enforces
    // the same with a 409 multi_production_user — this checkbox-gate is
    // belt-and-braces.
    (!multiProduction || cascadeAllProductions);

  async function handleConfirm() {
    setExecuting(true);
    setError(null);
    try {
      const body = {
        // Server-side type-to-confirm gate (users.js POST /:id/offboard).
        // Must be sent — `confirmNameMatches` only gates the button; the
        // server is the authority and rejects the request without this.
        confirm_name: confirmName,
        cascade_all_productions: cascadeAllProductions,
        erasure_request: erasureRequest,
      };
      // 🔑 ONLY SENT WHEN THE GUARD ACTUALLY DEMANDED SOMETHING. Since
      // 2026-09-21 the server refuses a department it did not ask about (400
      // `unrequested_buyer_replacement`), so an empty object is the correct
      // payload for an offboard that strands nobody — not a stale map left
      // over from a previous render.
      if (replacementsRequired.length > 0) {
        body.reassignments = {
          buyer_replacements: Object.fromEntries(
            replacementsRequired
              .filter((dep) => buyerReplacements[dep.department_id])
              .map((dep) => [dep.department_id, Number(buyerReplacements[dep.department_id])])
          ),
        };
      }
      const res = await apiPost(`/api/users/${user.id}/offboard`, body);
      const data = await res.json();
      if (!res.ok) {
        // 409 multi_production_user is the server-side cascade gate from
        // bd5e916. It shouldn't fire if the checkbox-gating above is
        // correct, but we surface a clearer message just in case.
        if (data.code === 'multi_production_user') {
          throw new Error('This user is on multiple productions. Tick "Cascade across all productions" to proceed.');
        }
        // 400 confirm_name_required. With confirm_name now on the wire this
        // can only realistically fire when the target's display_name changed
        // after this modal loaded (renamed in another tab/session), so the
        // admin needs the server's `expected` value — the raw server text
        // names wire fields (`confirm_name`/`display_name`) and tells them
        // nothing actionable.
        if (data.code === 'confirm_name_required') {
          throw new Error(
            data.expected
              ? `Confirmation name didn’t match. This user is now named “${data.expected}” — close and reopen this dialog, then type that exactly.`
              : 'Confirmation name didn’t match. Close and reopen this dialog, then type the user’s display name exactly.'
          );
        }
        // 409 with a department id: a department needs a successor and this
        // request did not name one. With the picker above that can only really
        // happen in a race — the department's other manager was deactivated,
        // or the target was seated somewhere new, after this dialog loaded.
        // Reopening re-runs the pre-flight and re-renders the picker.
        if (data.missing_buyer_department_id) {
          throw new Error(
            `${data.error} Close and reopen this dialog to pick a replacement — the department's `
            + 'members have changed since it loaded.'
          );
        }
        // 400: this request named a department the server never asked about.
        // The picker only ever offers demanded departments, so reaching this
        // means the pre-flight in hand is stale.
        if (data.code === 'unrequested_buyer_replacement') {
          throw new Error(
            'This dialog is out of date — it offered a department that no longer needs a '
            + 'replacement. Close and reopen it, then try again.'
          );
        }
        throw new Error(data.error || 'Deactivation failed');
      }
      onSuccess?.(data);
      onClose();
    } catch (err) {
      setError(err.message);
      setExecuting(false);
    }
  }

  // Defensive client-side gate. Server is the source of truth.
  if (!isGlobalAdmin) {
    return (
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 ${MODAL_OVERLAY}`}
        onClick={onClose}
        data-testid="global-offboard-modal"
      >
        <div
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full ${MODAL_PANEL_MOBILE_CAP}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <h3 className="text-lg font-semibold text-red-600 dark:text-red-400">Not permitted</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Only a site admin can deactivate users globally.
            </p>
          </div>
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button type="button" onClick={onClose} className={BTN.secondary}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 ${MODAL_OVERLAY}`}
      onClick={onClose}
      data-testid="global-offboard-modal"
    >
      <div
        className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto ${MODAL_PANEL_MOBILE_CAP}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-red-600 dark:text-red-400">
            Deactivate {user.display_name} globally
          </h3>
        </div>

        <div className="p-6 space-y-4">
          {/* Nuclear-path banner */}
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700">
            <p className="text-sm font-semibold text-red-700 dark:text-red-300 mb-1">
              This is the nuclear path
            </p>
            <p className="text-xs text-red-700 dark:text-red-300">
              {user.display_name} will lose access to ALL productions and their account will be
              deactivated. Use &ldquo;Remove from production&rdquo; for normal departures.
            </p>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-6">
              <LoadingSpinner />
            </div>
          )}

          {!loading && error && !preflight && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-sm text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
              {error}
            </div>
          )}

          {preflight && (
            <>
              {hardBlocks.length > 0 && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                  <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">
                    Cannot deactivate
                  </p>
                  <ul className="text-sm text-red-600 dark:text-red-400 list-disc pl-5 space-y-0.5">
                    {hardBlocks.map((msg, i) => <li key={i}>{msg}</li>)}
                  </ul>
                </div>
              )}

              {/* ⚖️ THE REPLACEMENT PICKER (Pete, 2026-09-20). Before this, a
                  department about to be left with nobody able to run its
                  orders produced a raw 409 with no way forward from the
                  dialog. It is an ACTIONABLE BLOCK: the admin picks a
                  successor here and the same Confirm button completes.

                  🪤 The departments listed are the ones the SERVER demanded,
                  and the payload carries only those. Offering a department the
                  guard did not ask about would now be refused (400
                  `unrequested_buyer_replacement`). */}
              {replacementsRequired.length > 0 && (
                <div
                  className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/25 border border-amber-300 dark:border-amber-700"
                  data-testid="global-offboard-replacements"
                >
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-1">
                    Who takes over{replacementsRequired.length === 1 ? ' this department' : ' these departments'}?
                  </p>
                  <p className="text-xs text-amber-800 dark:text-amber-300 mb-3">
                    {user.display_name} is the only active person who can run{' '}
                    {replacementsRequired.length === 1 ? 'it' : 'them'}. Choose someone to hand
                    over to — they will be given the same rights over that department&rsquo;s orders.
                  </p>
                  <div className="space-y-3">
                    {replacementsRequired.map((dep) => (
                      <div key={dep.department_id}>
                        <label
                          className="block text-xs font-medium text-amber-900 dark:text-amber-200 mb-1"
                          htmlFor={`offboard-replacement-${dep.department_id}`}
                        >
                          {dep.department_name}
                        </label>
                        {Array.isArray(dep.candidates) && dep.candidates.length > 0 ? (
                          <select
                            id={`offboard-replacement-${dep.department_id}`}
                            data-testid={`global-offboard-replacement-${dep.department_id}`}
                            value={buyerReplacements[dep.department_id] || ''}
                            onChange={(e) => setBuyerReplacements((prev) => ({
                              ...prev,
                              [dep.department_id]: e.target.value,
                            }))}
                            className="w-full text-sm rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5"
                          >
                            <option value="">Select a replacement…</option>
                            {dep.candidates.map((c) => (
                              <option key={c.user_id} value={c.user_id}>
                                {c.display_name} ({c.role})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-xs text-red-700 dark:text-red-300">
                            No other active member to hand this department to.
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Productions list — admin can see other productions because they
                  have global oversight. Buyers never see this modal. */}
              {Array.isArray(preflight.productions) && preflight.productions.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    This user is currently assigned to these productions:
                  </h4>
                  <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 pl-1">
                    {preflight.productions.map((p) => (
                      <li key={p.id} className="flex items-baseline gap-2">
                        <span className="font-medium text-gray-700 dark:text-gray-300">{p.name}</span>
                        <span className="text-gray-400 dark:text-gray-500">
                          ({p.department_count} dept{p.department_count === 1 ? '' : 's'}: {p.department_names.join(', ')})
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                    Personal &ldquo;My Space&rdquo; workspaces are preserved with the deactivated account.
                  </p>
                </div>
              )}

              {/* Linked-data blast radius */}
              {preflight.linkedData && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Linked data
                  </h4>
                  <div className="text-sm space-y-1 text-gray-600 dark:text-gray-400">
                    <LinkedRow label="Department assignments" value={preflight.linkedData.department_assignments} note="removed across all productions" />
                    <LinkedRow label="Orders created" value={preflight.linkedData.orders} preserved />
                    <LinkedRow label="Pending approvals" value={preflight.linkedData.pending_approvals} note="auto-advanced to next step" />
                    <LinkedRow label="Historical approval steps" value={(preflight.linkedData.order_steps_as_approver || 0) - (preflight.linkedData.pending_approvals || 0)} note="approver_user_id cleared" />
                    <LinkedRow label="Workflow template steps (as approver)" value={preflight.linkedData.workflow_steps_as_approver} note="step rows deleted" />
                    <LinkedRow label="Personal approval workflows" value={preflight.linkedData.personal_workflows} note="cascade deleted" />
                    <LinkedRow
                      label="Equipment listings"
                      value={preflight.linkedData.equipment_listings}
                      note={erasureRequest ? 'PERMANENTLY deleted (erasure request)' : 'preserved (tick erasure to delete)'}
                      danger={erasureRequest}
                      preserved={!erasureRequest}
                    />
                    <LinkedRow label="Teams created (creator≠lead)" value={preflight.linkedData.teams_created_auto_transfer} note="creator transferred to lead" />
                    <LinkedRow label="Teams created (creator==lead)" value={preflight.linkedData.teams_created_stale} note="becomes stale (members retain view)" warn />
                    {/* Owner ruling 2026-09-03: marked inactive IN PLACE, never
                        deleted — history survives, lists stop offering them. */}
                    <LinkedRow label="Team memberships" value={preflight.linkedData.team_memberships} note="marked inactive (kept for history)" preserved />
                    <LinkedRow label="Task assignments" value={preflight.linkedData.task_assignments} note="marked inactive (kept for history)" preserved />
                    <LinkedRow label="Active sessions" value={preflight.linkedData.active_sessions} note="revoked" />
                  </div>
                </div>
              )}

              {/* Cascade checkbox — required when multi-prod. Always rendered so
                  the admin sees the option even on single-prod users. */}
              <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/20">
                <input
                  type="checkbox"
                  checked={cascadeAllProductions}
                  onChange={(e) => setCascadeAllProductions(e.target.checked)}
                  className="mt-0.5"
                  data-testid="global-offboard-cascade"
                />
                <span className="text-xs text-red-700 dark:text-red-300">
                  <strong>Cascade across all productions</strong> (required to confirm — without this,
                  the API will reject the request when the user is in multiple productions)
                </span>
              </label>

              {/* GDPR erasure checkbox */}
              <label
                className="flex items-start gap-2 cursor-pointer"
                title="Permanently deletes the user's marketplace listings. Without this, listings are preserved but hidden via deactivated status."
              >
                <input
                  type="checkbox"
                  checked={erasureRequest}
                  onChange={(e) => setErasureRequest(e.target.checked)}
                  className="mt-0.5"
                  data-testid="global-offboard-erasure"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Also erase personal Hireforge listings (GDPR Art 17 erasure)
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Permanently deletes the user&rsquo;s marketplace listings. Without this, listings
                    are preserved but hidden via deactivated status.
                  </p>
                </div>
              </label>

              {/* Type-to-confirm gate */}
              <div>
                <label className={FIELD_LABEL_CLASS}>
                  Type <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1 rounded">{user.display_name}</span> to confirm:
                </label>
                <input
                  type="text"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder="Type the user's full display name"
                  className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200"
                  data-testid="global-offboard-confirm-input"
                />
                {confirmName.length > 0 && !confirmNameMatches && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    Doesn&rsquo;t match. Confirm must be exact, case-sensitive.
                  </p>
                )}
              </div>

              {/* Reactivation-limit notice — sets expectations up front. The
                  Reactivate button in PeopleManager only flips users.status;
                  department assignments wiped here are not auto-restored. */}
              <p className="text-xs italic text-gray-500 dark:text-gray-400">
                Note: reactivation later restores login only — productions must be re-added manually.
                The audit log records every production this user was on, for your reference.
              </p>
            </>
          )}

          {error && preflight && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-sm text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className={BTN.secondary}
            disabled={executing}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={`${BTN.danger} ${!canConfirm ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="global-offboard-confirm"
          >
            {executing ? 'Working…' : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Row in the linked-data summary. Value=0 rows are hidden. */
function LinkedRow({ label, value, note, preserved, warn, danger }) {
  if (!value) return null;
  const colorClass = danger
    ? 'text-red-600 dark:text-red-400'
    : warn
      ? 'text-amber-600 dark:text-amber-400'
      : preserved
        ? 'text-gray-600 dark:text-gray-400'
        : 'text-gray-600 dark:text-gray-400';
  return (
    <div className="flex items-start justify-between gap-3">
      <span className={colorClass}>
        <strong>{value}</strong> {label}
      </span>
      {note && <span className="text-xs text-gray-500 dark:text-gray-500 text-right flex-shrink-0">{note}</span>}
    </div>
  );
}
